/* Built In Imports */
import { useEffect, useRef, useState } from 'react';

/* External Imports */
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { Box, Collapse, Link } from '@chakra-ui/react';
import parser from 'html-react-parser';
import forEach from 'lodash/forEach';
import { useMediaQuery } from 'react-responsive';

/* Internal Imports */
/* Components */
import { refineUrlDomain } from '@components/Utility/utils';
import { useRouter } from 'next/router';
import { mixPanel } from '@utils/Mixpanel';

let allTags = ['readmore'];

const StructuredTextParser = ({
  sectionContent = {},
  str,
  mt,
  className = '',
  textAlign,
  region,
  lang,
  maxW = '',
  inlineReadMoreStyle = 'inline',
  ...props
}) => {
  const [readMore, setReadMore] = useState(true);
  const structured = useRef();
  const isMobile = useMediaQuery({ maxWidth: 767 });

  if (textAlign && textAlign === 'center-align') {
    textAlign = 'center';
  }
  const router = useRouter();
  let url = router.asPath;

  let innerText = '';
  let initialString = '';
  let startTagIndex = -1;

  // URL processing
  const re = new RegExp('href="([^"]+)"', 'g');
  let strMatched = str?.match(re);
  if (strMatched?.length) {
    forEach(strMatched, function (matched) {
      matched = matched.replace('href=', '').replace(/"/g, '');
      if (/^tel:/.test(matched)) return;
      str = str.replace(
        `"${matched}"`,
        `"${refineUrlDomain(matched, region, lang)}"`
      );
    });
  }

  // Process readmore tags
  forEach(allTags, function (tag) {
    let matched = str?.match(/\[(.*?)\]/g);
    if (matched?.length && matched[0].indexOf(tag) > -1) {
      startTagIndex = str.indexOf(matched[0]);
    }
    initialString = str;

    if (
      (sectionContent?.fieldId === 'read-more-ys' ||
        sectionContent?.fieldId === 'description-text-with-read-more') &&
      !isMobile
    ) {
      startTagIndex = -1;
      initialString = initialString
        .replace('[readmore]', '')
        .replace('[/readmore]', '');
    }
    initialString = str?.substring(0, startTagIndex) || initialString;

    if (startTagIndex && startTagIndex > -1) {
      let endTagStartIndex = str.indexOf(`[/${tag}]`);
      let endTagEndIndex = endTagStartIndex + tag.length + 3;
      let tagTextString = str?.substring(startTagIndex, endTagEndIndex);
      innerText = tagTextString
        .replace(`[${tag}]`, '')
        .replace(`[/${tag}]`, '')
        .replace(matched[0], '');
    }
  });

  useEffect(
    () => () => {
      setReadMore(true);
    },
    [str]
  );

  // Process content for seamless inline read more
  const processContentForInlineReadMore = () => {
    const isBlockStyle = inlineReadMoreStyle === 'block';
    const displayStyle = isBlockStyle
      ? 'display: block; margin-top: 8px;'
      : 'display: inline;';

    if (
      startTagIndex > -1 &&
      sectionContent?.fieldId === 'description-text-with-read-more'
    ) {
      if (readMore) {
        let processedText = initialString.trim();

        // Check if the text ends with a closing tag like </p>, </div>, etc.
        const lastTagMatch = processedText.match(/<\/([^>]+)>$/);

        if (lastTagMatch) {
          // Insert read more before the closing tag
          const readMoreSpan = `${
            isBlockStyle ? '...' : '<span style="color: inherit;">...</span>'
          }<span style="color: #2B251E; text-decoration: underline; cursor: pointer; ${displayStyle} padding: 0; white-space: nowrap; font-size: inherit; line-height: inherit; font-weight: 600;" class="inline-read-more">Read More</span>`;

          processedText = processedText.replace(
            /<\/([^>]+)>$/,
            readMoreSpan + '</' + lastTagMatch[1] + '>'
          );
        } else {
          // Wrap everything in a span to ensure inline behavior
          const readMoreSpan = `${
            isBlockStyle ? '...' : '<span style="color: inherit;">...</span>'
          }<span style="color: #2B251E; text-decoration: underline; cursor: pointer; ${displayStyle} padding: 0; white-space: nowrap; font-size: inherit; line-height: inherit; font-weight: 600;" class="inline-read-more">Read More</span>`;

          processedText =
            '<span style="display: inline;">' +
            processedText +
            readMoreSpan +
            '</span>';
        }

        return processedText;
      } else {
        let fullText = (initialString + innerText)
          .replace(/\[readmore\]/g, '')
          .replace(/\[\/readmore\]/g, '');
        fullText = fullText.trim();

        // Find the last closing tag and insert read less before it
        const lastTagMatch = fullText.match(/<\/([^>]+)>$/);

        if (lastTagMatch) {
          const readLessSpan = isBlockStyle
            ? `<span style="display: block; margin-top: 12px;"><span style="color: #2B251E; text-decoration: underline; cursor: pointer; padding: 0; white-space: nowrap; font-size: inherit; line-height: inherit; font-weight: 600;" class="inline-read-less">Read Less</span></span>`
            : `<span style="color: #2B251E; text-decoration: underline; cursor: pointer; ${displayStyle} padding: 0; white-space: nowrap; font-size: inherit; line-height: inherit; font-weight: 500;" class="inline-read-less"> Read Less</span>`;

          fullText = fullText.replace(
            /<\/([^>]+)>$/,
            readLessSpan + '</' + lastTagMatch[1] + '>'
          );
        } else {
          const readLessSpan = isBlockStyle
            ? `<span style="display: block; margin-top: 12px;"><span style="color: #2B251E; text-decoration: underline; cursor: pointer; padding: 0; white-space: nowrap; font-size: inherit; line-height: inherit; font-weight: 600;" class="inline-read-less">Read Less</span></span>`
            : `<span style="color: #2B251E; text-decoration: underline; cursor: pointer; ${displayStyle} padding: 0; white-space: nowrap; font-size: inherit; line-height: inherit; font-weight: 500;" class="inline-read-less"> Read Less</span>`;

          fullText =
            '<span style="display: inline;">' +
            fullText +
            readLessSpan +
            '</span>';
        }

        return fullText;
      }
    }
    return initialString;
  };

  // Handle click on inline read more/less
  useEffect(() => {
    const handleReadMoreClick = e => {
      if (e.target.classList.contains('inline-read-more')) {
        e.preventDefault();
        setReadMore(false);
      }
      if (e.target.classList.contains('inline-read-less')) {
        e.preventDefault();
        setReadMore(true);
      }
    };

    document.addEventListener('click', handleReadMoreClick);
    return () => document.removeEventListener('click', handleReadMoreClick);
  }, []);

  return (
    <Box ref={structured}>
      <Box
        className={
          textAlign === 'center'
            ? `${className || ''} centreText`
            : className || ''
        }
        mx={{ base: '0', md: 'auto', lg: 'auto' }}
        mt={{ base: mt || '15px', md: mt || '15px', lg: mt || '15px' }}
        maxW={{
          base: '100%',
          sm: maxW || '100%',
          md: maxW || '515px',
          lg:
            maxW ||
            sectionContent?.fieldId === 'read-more-ys' ||
            sectionContent?.fieldId === 'description-text-with-read-more'
              ? maxW || '590px'
              : '600px',
          xl:
            maxW ||
            sectionContent?.fieldId === 'read-more-ys' ||
            sectionContent?.fieldId === 'description-text-with-read-more'
              ? maxW || '590px'
              : '100%',
        }}
        textAlign={textAlign || 'left'}
        fontSize={'1rem'}
        px={0}
        sx={{
          'p + *': {
            marginTop: '1.5rem',
          },
        }}
        {...props}
      >
        {/* For description-text-with-read-more - seamless inline behavior */}
        {sectionContent?.fieldId === 'description-text-with-read-more' ? (
          <Box
            display="block"
            lineHeight="inherit"
            sx={
              inlineReadMoreStyle === 'block'
                ? {
                    // For 'block' style on mobile, allow paragraphs to be blocks
                    '& p': { display: 'block' },
                    'p + p': {
                      marginTop: '1rem',
                    },
                  }
                : {
                    // Default to original inline behavior
                    '& p, & div': {
                      display: 'inline !important',
                      margin: 0,
                    },
                  }
            }
          >
            {parser(processContentForInlineReadMore())}
          </Box>
        ) : (
          // Default behavior for other field IDs
          <>
            {initialString && parser(initialString)}

            {/* Inline Read More link for non-description-text-with-read-more fields */}
            {startTagIndex > -1 &&
              readMore &&
              sectionContent?.fieldId !== 'read-more-ys' &&
              sectionContent?.fieldId !== 'description-text-with-read-more' && (
                <Link
                  as="span"
                  fontFamily={
                    lang === 'ta' ? 'MuktaMlalar' : 'FedraSansStd-medium'
                  }
                  color={'#2B251E !important'}
                  fontSize="16px !important"
                  textDecoration="underline !important"
                  cursor="pointer"
                  onClick={() => setReadMore(false)}
                >
                  ...Read More
                </Link>
              )}
          </>
        )}
      </Box>

      {/* Collapsible content for non-description-text-with-read-more fields */}
      {startTagIndex > -1 &&
        sectionContent?.fieldId !== 'description-text-with-read-more' && (
          <Box
            mx="auto"
            mt={{ base: mt || '10px', md: mt || '30px', lg: mt || '30px' }}
            maxW={{
              base: '100%',
              md: '515px',
              lg:
                maxW || sectionContent?.fieldId === 'read-more-ys'
                  ? '590px'
                  : '600px',
              xl:
                maxW || sectionContent?.fieldId === 'read-more-ys'
                  ? '590px'
                  : '770px',
            }}
          >
            <Collapse in={!readMore} animateOpacity duration={500}>
              <Box
                fontFamily={lang === 'ta' ? 'MuktaMlalar' : 'FedraSansStd-book'}
                pb="30px"
                className={`${className || ''}`}
                textAlign={textAlign || 'left'}
                {...props}
              >
                {parser(innerText)}

                {/* Inline Read Less for non-description-text-with-read-more fields */}
                {sectionContent?.fieldId !== 'read-more-ys' && (
                  <Link
                    as="span"
                    fontFamily={
                      lang === 'ta' ? 'MuktaMlalar' : 'FedraSansStd-medium'
                    }
                    color={'#2B251E !important'}
                    fontSize="16px !important"
                    textDecoration="underline !important"
                    cursor="pointer"
                    onClick={() => setReadMore(true)}
                    mt={1}
                  >
                    Read Less
                  </Link>
                )}
              </Box>
            </Collapse>

            {/* Button-style links for read-more-ys field ID */}
            <Box textAlign={'center'} position="relative">
              {sectionContent?.fieldId === 'read-more-ys' && readMore && (
                <Link
                  display="inline-block"
                  mb="20px"
                  fontFamily={
                    lang === 'ta' ? 'MuktaMlalar' : 'FedraSansStd-medium'
                  }
                  borderRadius="3px"
                  borderBottom="none !important"
                  bgColor="transparent"
                  border="none"
                  color={'#721B0F !important'}
                  fontSize={{ base: '16px !important', md: '18px !important' }}
                  padding="10px 35px"
                  textDecoration="none !important"
                  transition="none !important"
                  _hover={{
                    textDecoration: 'none',
                    bgColor: '#ffffff !important',
                    color: '#D04723 !important',
                    bgSize: '0% 0% !important',
                  }}
                  textAlign="center"
                  width={'200px'}
                  onClick={() => setReadMore(false)}
                >
                  ...Read More <ChevronDownIcon w={7} h={10} />
                </Link>
              )}

              {sectionContent?.fieldId === 'read-more-ys' && !readMore && (
                <Link
                  display="inline-block"
                  mb="20px"
                  fontFamily={
                    lang === 'ta' ? 'MuktaMlalar' : 'FedraSansStd-medium'
                  }
                  borderRadius="3px"
                  borderBottom="none !important"
                  bgColor="transparent"
                  border="none"
                  color={'#721B0F !important'}
                  fontSize={{ base: '16px !important', md: '18px !important' }}
                  padding="10px 35px"
                  textDecoration="none !important"
                  transition="none !important"
                  _hover={{
                    textDecoration: 'none',
                    bgColor: '#ffffff !important',
                    color: '#D04723 !important',
                    bgSize: '0% 0% !important',
                  }}
                  textAlign="center"
                  width={'200px'}
                  onClick={() => setReadMore(true)}
                >
                  Read Less <ChevronUpIcon w={7} h={10} />
                </Link>
              )}
            </Box>
          </Box>
        )}

      <style jsx global>{`
        .centreText ul {
          display: inline-block;
        }

        .inline-read-more,
        .inline-read-less {
          display: inline !important;
          margin: 0 !important;
          padding: 0 !important;
          line-height: inherit !important;
          white-space: nowrap !important;
          transition: color 0.2s ease;
          font-size: inherit !important;
          vertical-align: baseline !important;
        }

        .inline-read-more:hover,
        .inline-read-less:hover {
          color: #1a1a1a !important;
        }

        /* Force inline behavior for description-text-with-read-more */
        .description-text-with-read-more-container p,
        .description-text-with-read-more-container div {
          display: inline !important;
          margin: 0 !important;
        }

        /* Prevent line breaks */
        .description-text-with-read-more-container {
          white-space: normal;
        }

        .description-text-with-read-more-container .inline-read-more,
        .description-text-with-read-more-container .inline-read-less {
          white-space: nowrap !important;
        }
      `}</style>
    </Box>
  );
};

export default StructuredTextParser;
